import { useEffect, useState } from "react";
import { Close } from "../../Icons/Icons";
import { getCookie } from "../../reducer/utils";
import PropTypes from 'prop-types';

const LiveOTTCategoryFilter = ({ showCategoryFilter, setShowCategoryFilter, onApplyFilter, filter }) => {
    
    const [providerNames, setProviderNames] = useState([]);
    const [eventTimeFrom, setEventTimeFrom] = useState(filter?.event_time?.gte || '');
    const [eventTimeTo, setEventTimeTo] = useState(filter?.event_time?.lte || '');
    const [subscription, setSubscription] = useState(filter?.is_subscribed ?? null);

    const [eventTitle, setEventTitle] = useState(filter?.event_title?.like || '');

    const baseURL = process.env.REACT_APP_BASE_URL;

    const getProviderNames = async () => {
        try {
            const response = await fetch(`${baseURL}/internal/api/v4/utility/providers`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${getCookie("token")}`,
                },
            });
            if (!response.ok) {
                // Keep the list empty on error (e.g. 403) so .map doesn't crash.
                setProviderNames([]);
                return;
            }
            const data = await response.json();
            setProviderNames(Array.isArray(data) ? data : []);
        } catch (error) {
            setProviderNames([]);
        }
    }
    
    const defaultProviderFilter = filter?.provider_name?.in_list?.reduce((acc, provider) => {
        acc[provider] = true;
        return acc;
    }, {}) || {};

    const [providerFilter, setProviderFilter] = useState(defaultProviderFilter);

    const clearFilter = () => {
        setProviderFilter(defaultProviderFilter);
        setEventTimeFrom('');
        setEventTimeTo('');
        setSubscription(null);
        onApplyFilter({});
    }
    
    const handleStatusChange = (event) => {
        const { id, checked } = event.target;
        setProviderFilter((prev) => ({ ...prev, [id]: checked }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onApplyFilter({
            providerFilter: providerFilter,
            eventTime: {
                gte: eventTimeFrom,
                lte: eventTimeTo,
            },
            subscription: subscription,
            eventTitle: eventTitle,
        });
    };

    useEffect(() => {
        getProviderNames();
    }, []);

    return (
        <div className={`fixed top-0 left-0 w-screen h-screen z-50 ${showCategoryFilter ? 'visible' : 'hidden'}`}>
            <div className="absolute bg-gray-800 bg-opacity-25 backdrop-blur w-full h-full" onClick={() => setShowCategoryFilter(false)}></div>
            <div className={`max-w-md w-full h-screen mx-auto bg-white rounded-lg shadow-lg absolute ${showCategoryFilter ? 'right-0' : '-right-full'} top-0`}>
                <div className="flex items-center justify-between p-5 border-b">
                    <h2 className="text-lg text-inherit font-semibold font-bold">Filters</h2>
                    <Close className='cursor-pointer' onClick={() => setShowCategoryFilter(false)} />
                </div>
                <form className="p-5 flex flex-col justify-between" style={{ height: 'calc(100vh - 70px)' }} onSubmit={handleSubmit}>
                    <div className="max-h-full overflow-auto">
                        
                        <p className="block text-gray-900 font-medium text-sm mb-2">Event Title</p>
                        <div className="flex items-start justify-start gap-3 mb-5">
                            <div className="w-full">
                                <input type="text" value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50" />
                            </div>
                        </div>

                        <p className="block text-gray-900 font-medium text-sm mb-2">Event Start Time</p>
                        <div className="flex items-start justify-start gap-3 mb-5">
                            <div className="w-full">
                                <label className="block text-gray-900 font-medium text-sm mb-2">From</label>
                                <input type="date" value={eventTimeFrom} onChange={(e) => setEventTimeFrom(e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50" />
                            </div>
                            <div className="w-full">
                                <label className="block text-gray-900 font-medium text-sm mb-2">To</label>
                                <input type="date" value={eventTimeTo} onChange={(e) => setEventTimeTo(e.target.value)} className="block w-full border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50" />
                            </div>
                        </div>

                        <label className="block text-gray-900 font-medium text-sm mb-2">Provider</label>
                        <div className="relative p-3 block w-full border border-gray-300 rounded-md shadow-sm px-4 py-3 bg-gray-50 mb-5 max-h-80 overflow-auto">
                            {providerNames.map((provider, index) => (
                                <div className="flex items-center mb-4" key={index}>
                                    <input id={provider} type="checkbox" checked={providerFilter[provider]} onChange={handleStatusChange} className="w-4 h-4 text-primary1 bg-gray-100 border-gray-300 rounded" />
                                    <label htmlFor={provider} className="ms-2 text-sm font-medium text-gray-900">{provider.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</label>
                                </div>
                            ))}
                        </div>

                        <label className="block text-gray-900 font-medium text-sm mb-2">Subscription Status</label>
                        <p className="block text-gray-900 font-medium text-sm mb-2 flex items-center pl-1 cursor-pointer">
                            <input id="all" name="subscription" type="radio" checked={subscription == null} onChange={(e) => setSubscription(null)} className="w-4 h-4 text-primary1 bg-gray-100 border-gray-300 rounded" />
                            <label htmlFor="all" className="ms-2 text-sm font-medium text-gray-900 cursor-pointer">All</label>
                        </p>
                        <p className="block text-gray-900 font-medium text-sm mb-2 flex items-center pl-1 cursor-pointer">
                            <input id="subscribed" name="subscription" type="radio" checked={subscription == true} onChange={(e) => setSubscription(true)} className="w-4 h-4 text-primary1 bg-gray-100 border-gray-300 rounded" />
                            <label htmlFor="subscribed" className="ms-2 text-sm font-medium text-gray-90 cursor-pointer">Subscribed</label>
                        </p>
                        <p className="block text-gray-900 font-medium text-sm mb-2 flex items-center pl-1 cursor-pointer">
                            <input id="unsubscribed" name="subscription" type="radio" checked={subscription == false} onChange={(e) => setSubscription(false)} className="w-4 h-4 text-primary1 bg-gray-100 border-gray-300 rounded" />
                            <label htmlFor="unsubscribed" className="ms-2 text-sm font-medium text-gray-900 cursor-pointer">Unsubscribed</label>
                        </p>

                    </div>
                    <div className="flex items-center justify-end pt-4 border-t gap-2">
                        <button type="submit" className="bg-primary1 text-white py-2 px-3 rounded-lg w-full">Apply filter</button>
                        <button type="reset" className="bg-white text-gray-900 border border-gray-200 py-2 px-3 rounded-lg w-full" onClick={() => clearFilter()}>Clear all</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

LiveOTTCategoryFilter.propTypes = {
    showCategoryFilter: PropTypes.bool.isRequired,
    setShowCategoryFilter: PropTypes.func.isRequired,
    onApplyFilter: PropTypes.func.isRequired,
    filter: PropTypes.shape({
        provider_name: PropTypes.shape({
            in_list: PropTypes.arrayOf(PropTypes.string)
        }),
        event_time: PropTypes.shape({
            gte: PropTypes.string,
            lte: PropTypes.string
        }),
        is_subscribed: PropTypes.bool
    })
};

export default LiveOTTCategoryFilter;
